import { apiVersion } from './apis.const';
import { transformResponse } from './apis.util';
import { baseApi } from './base.api';

/**
 * API Urls
 */
const decalIdUrl = ({ orgSlug, id }: { orgSlug: string; id: number }) => `${orgSlug}/${apiVersion}/decals/${id}`;
const decalApproveUrl = ({ orgSlug, id }: { orgSlug: string; id: number }) =>
  `${orgSlug}/${apiVersion}/decals/${id}/approve`;
const decalUnapproveUrl = ({ orgSlug, id }: { orgSlug: string; id: number }) =>
  `${orgSlug}/${apiVersion}/decals/${id}/unapprove`;

/**
 * Create APIs
 */
export const decalApi = baseApi.injectEndpoints({
  overrideExisting: false,

  endpoints: (build) => ({
    getDecal: build.query<GetDecalRes, GetDecalReq>({
      query: ({ orgSlug, id }) => ({
        url: `${decalIdUrl({ orgSlug, id })}?format=svg`,
        responseHandler: (response) => response.text(),
      }),
      providesTags: (result, error, { id }) => [{ type: 'Zone', id }, 'GetDecal'],
    }),

    downloadDecal: build.query<DownloadDecalRes, DownloadDecalReq>({
      query: ({ orgSlug, id, format }) => ({
        url: `${decalIdUrl({ orgSlug, id })}?format=${format}`,
        headers: {
          Accept: 'application/octet-stream',
        },
        responseHandler: (response) => response.arrayBuffer(),
      }),
    }),

    approveDecal: build.mutation<ApproveDecalRes, ApproveDecalReq>({
      query: ({ orgSlug, id }) => ({
        url: decalApproveUrl({ orgSlug, id }),
        method: 'POST',
      }),
      transformResponse,
      invalidatesTags: ['Zones'],
    }),

    unapproveDecal: build.mutation<UnapproveDecalRes, UnapproveDecalReq>({
      query: ({ orgSlug, id }) => ({
        url: decalUnapproveUrl({ orgSlug, id }),
        method: 'DELETE',
      }),
      transformResponse,
    }),
  }),
});

/**
 * Hooks
 */
export const { useGetDecalQuery, useLazyDownloadDecalQuery, useApproveDecalMutation, useUnapproveDecalMutation } =
  decalApi;

/**
 * Types
 */
type GetDecalRes = any;
type GetDecalReq = any;
type DownloadDecalRes = any;
type DownloadDecalReq = { orgSlug: string; id: number; format: 'svg' | 'jpeg' | 'png' | 'pdf' };
type ApproveDecalRes = any;
type ApproveDecalReq = any;
type UnapproveDecalRes = any;
type UnapproveDecalReq = any;
